import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Hook to fetch and cache dynamic SVG assets from the backend.
 * These assets contain colors and styles derived from AI analysis of product photos.
 */
export const useDynamicAssets = () => {
    const [dynamicAssets, setDynamicAssets] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                const response = await fetch(`${API_URL}/api/configuration/dynamic-assets`);
                if (!response.ok) {
                    throw new Error('Failed to fetch dynamic assets');
                }
                const data = await response.json();
                if (data.success) {
                    // Only update if data actually changed to avoid unnecessary re-renders (deep comparison or simple check)
                    // For now, simple set is fine as React handles identity checks often, but objects are new references.
                    // To prevent infinite loop if we added it to dependency array, but here we invoke in interval.
                    setDynamicAssets(prev => {
                        if (JSON.stringify(prev) !== JSON.stringify(data.assets)) {
                            return data.assets;
                        }
                        return prev;
                    });
                }
            } catch (err) {
                console.warn('Dynamic assets not available, using defaults:', err.message);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAssets(); // Initial fetch

        // Poll every 30 seconds to check for updates from admin panel
        const intervalId = setInterval(fetchAssets, 30000);

        return () => clearInterval(intervalId);
    }, []);

    return { dynamicAssets, loading, error };
};

/**
 * Get colors for a specific component type from dynamic assets.
 * Falls back to default colors if not available.
 */
export const getComponentColors = (dynamicAssets, componentType, defaultColors) => {
    if (!dynamicAssets?.components?.[componentType]?.colors) {
        return defaultColors;
    }

    const dynamicColors = dynamicAssets.components[componentType].colors;

    return {
        ...defaultColors,
        aluminum: dynamicColors.primary || defaultColors.aluminum,
        aluminumLight: dynamicColors.highlight || defaultColors.aluminumLight,
        aluminumDark: dynamicColors.shadow || defaultColors.aluminumDark,
        aluminumOutline: dynamicColors.outline || defaultColors.aluminumOutline,
        frame: dynamicColors.secondary || defaultColors.frame,
        frameOutline: dynamicColors.outline || defaultColors.frameOutline
    };
};

/**
 * Get style properties for a specific component type from dynamic assets.
 */
export const getComponentStyles = (dynamicAssets, componentType) => {
    if (!dynamicAssets?.components?.[componentType]?.style) {
        return null;
    }

    return dynamicAssets.components[componentType].style;
};

export default useDynamicAssets;
